import { FC, ReactNode, useEffect, useState } from 'react';
import styles from './SuggestionLayout.module.scss';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import { RxUtils, Tr } from '@utils';
import { CarModel, simulationService, SimulationTask, SimulationVehicle } from '@services';
import { Box, Divider, Grid, Stack, StackProps, Typography } from '@mui/material';
import { Searchbox } from '@components/common';
import { VoltaageSuggestsCard } from '../VoltaageSuggestsCard';
import { EmptyEntityPageLayout } from '@components/portal/common';
import { ReactComponent as Illustration } from '@assets/illustrations/no-vehicle-selected-for-comparison.svg';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ISelectableSingleModelnfoProps, SelectableSingleModelnfo } from '../SelectableSingleModelInfo';

interface ISuggestionLayoutProps extends Omit<StackProps, 'title' | 'left'> {
	title: ReactNode;
	topRight: ReactNode;
	left: ReactNode;
	cta: ReactNode;
	selectedCarModels?: CarModel[];
	task?: SimulationTask;
	vehiclePriceVisible?: boolean;
	onSelectedCarModelChange?: ISelectableSingleModelnfoProps['onSelectChange'];
}

export const SuggestionLayout: FC<ISuggestionLayoutProps> = ({
	title,
	topRight,
	left,
	cta,
	task,
	selectedCarModels,
	vehiclePriceVisible,
	onSelectedCarModelChange
}) => {
	const { t } = useTranslation('portal');
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<SimulationVehicle[]>([]);
	const [sliderRef, setSliderRef] = useState<Slider>();

	useEffect(() => {
		if (searchText && task) {
			RxUtils.promisify(simulationService.searchVehicles(task, searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	return (
		<Stack direction="column" sx={{ height: 1.0, overflowY: 'hidden' }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ paddingX: 6.5, minHeight: 88 }}>
				<Typography variant="h3" sx={{ fontWeight: 'bold' }}>
					{title}
				</Typography>
				{topRight}
			</Stack>
			<Divider sx={{ opacity: 0.4 }} />
			<Grid container direction="row" sx={{ flexGrow: 1, overflow: 'hidden' }}>
				<Grid item xs={3.25}>
					{left}
				</Grid>
				<Grid item xs={8.75} sx={{ maxHeight: 1.0, display: 'flex', overflow: 'hidden' }}>
					{task ? (
						<Stack
							direction="column"
							spacing={3}
							sx={{ paddingTop: 3, paddingBottom: 5, paddingX: 10, maxHeight: 1.0, width: 1.0, overflowY: 'hidden' }}
						>
							<Typography variant="h4" sx={{ fontWeight: 600 }}>
								<Tr.Portal path="conversion-list.other-solutions" />
							</Typography>
							<Searchbox
								testId="vehicle-suggestion-search-box"
								alwaysExpanded
								placeholder={t('conversion-list.search-solutions')}
								sx={{ width: 410 }}
								searchText={searchText}
								onSearchTextChange={setSearchText}
							/>
							<Typography variant="h4" sx={{ fontWeight: 600 }}>
								{searchText ? (
									<Tr.Portal
										path={searchResult?.length > 1 ? 'conversion-list.results-found' : 'conversion-list.result-found'}
										options={{ length: searchResult?.length || 0 }}
									/>
								) : (
									<Tr.Portal path="conversion-list.voltaage-suggests" />
								)}
							</Typography>
							<Box sx={{ display: 'flex', flexGrow: 1, maxHeight: 1.0, maxWidth: 1.0, overflowY: 'hidden' }}>
								{searchText ? (
									<Stack direction="row" alignItems="center" spacing={4} sx={{ flexGrow: 1, maxHeight: 1.0, width: 1.0 }}>
										{searchResult.length > 0 && (
											<div className={styles.SliderContainer}>
												<Slider
													ref={(ref) => ref && !sliderRef && setSliderRef(ref)}
													dots={false}
													arrows
													infinite={false}
													draggable
													speed={500}
													slidesToShow={2}
													slidesToScroll={2}
													prevArrow={
														<Box sx={{ backgroundColor: 'primary.main' }}>
															<ArrowBackIosNewIcon sx={{ color: 'common.white', width: 16, height: 16 }} />
														</Box>
													}
													nextArrow={
														<Box sx={{ backgroundColor: 'primary.main' }}>
															<ArrowForwardIosIcon sx={{ color: 'common.white', width: 16, height: 16 }} />
														</Box>
													}
												>
													{searchResult.map((simulationVehicle, i) => {
														return (
															<Box key={`model-${simulationVehicle.carModelId}-${i}`} sx={{ paddingX: 1 }}>
																<SelectableSingleModelnfo
																	vehicle={task.fleetVehicle}
																	simulationVehicle={simulationVehicle}
																	selected={
																		!!selectedCarModels?.find(
																			(v) => v.id === simulationVehicle.carModelId
																		)
																	}
																	vehiclePriceVisible={vehiclePriceVisible}
																	onSelectChange={onSelectedCarModelChange}
																/>
															</Box>
														);
													})}
												</Slider>
											</div>
										)}
									</Stack>
								) : (
									<VoltaageSuggestsCard
										task={task}
										vehiclePriceVisible={vehiclePriceVisible}
										selectedCarModels={selectedCarModels}
										onSelectionChange={onSelectedCarModelChange}
									/>
								)}
							</Box>
							<Stack alignItems="center">{cta}</Stack>
						</Stack>
					) : (
						<EmptyEntityPageLayout
							illustration={<Illustration />}
							content={<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-suggestions.empty-selection-text" />}
						/>
					)}
				</Grid>
			</Grid>
		</Stack>
	);
};
